import { useState } from "react";
import {
  useCheckIngredientInMapping,
  useDeleteIngredient,
  useGetIngredientList,
  usePutSupplierData
} from "../../../../api/ingredients";
import { ROLES } from "../../../../config/permission-maps";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { IngredientRowMapper } from "../services/IngredientRowMapper";
import { IngredientListFilters, IngredientListViewModel } from "../types/ingredientListView.types";

/**
 * Owns every piece of state, data fetching and mutation handling that the
 * legacy `IngredientList` kept inline (see
 * src/screens/ingredients/ingredientList/IngredientList.tsx). Presentational
 * components consume the `IngredientListViewModel` this hook returns instead
 * of importing `api/ingredients` themselves — the same dependency-inversion
 * split used elsewhere in this codebase's Tailwind migrations.
 */
export const useIngredientListViewModel = (): IngredientListViewModel => {
  const [visibleRegisterModal, setVisibleRegisterModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [viewIngredientData, setViewIngredientData] = useState<any>(undefined);
  const [editIngredientData, setEditIngredientData] = useState<any>(undefined);
  const [deleteIngredientData, setDeleteIngredientData] = useState<any>({});
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [searchKey, setSearchKey] = useState("");
  const [filters, setFilters] = useState<IngredientListFilters>({
    ingredientIds: [],
    supplierIds: [],
    dataTypes: [],
    sortBy: 3
  });

  const localUserRole = getUserRolesInLocal();
  const isUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const {
    data: ingredientList,
    isLoading: isRowsLoading,
    refetch: refetchIngredients
  } = useGetIngredientList(pageNumber, pageLimit, searchKey, filters);

  const { isLoading: isPutSupplierLoading, mutateAsync: mutatePutSupplier } = usePutSupplierData();
  const { isLoading: isMappingCheckLoading, mutate: isIngredientInMapping } =
    useCheckIngredientInMapping();
  const { isLoading: isDeleteLoading, mutate: deleteIngredient } = useDeleteIngredient();

  const refetchWithFilters = (
    newIngredientIds: string[],
    newSupplierIds: string[],
    newDataTypeIds: string[],
    sortBy: number
  ) => {
    setFilters({
      ingredientIds: newIngredientIds,
      supplierIds: newSupplierIds,
      dataTypes: newDataTypeIds,
      sortBy
    });
  };

  const deleteSupplier = () => {
    const remainingSupplierIngredients = (deleteIngredientData.supplierIngredientIds || []).filter(
      (id: any) => id != deleteIngredientData.ingredient_id
    );
    mutatePutSupplier(
      {
        _id: deleteIngredientData.supplier_id,
        supplier_name: deleteIngredientData.supplier_name,
        ingredientIds: remainingSupplierIngredients || []
      },
      {
        onSuccess() {
          successMessage("Supplier removed from ingredient successfully");
          setDeleteIngredientData({});
        }
      }
    );
  };

  const confirmDelete = () => {
    deleteSupplier();
    deleteIngredient(deleteIngredientData, {
      onSuccess() {
        setDeleteIngredientData({});
        refetchIngredients();
      }
    });
    setVisibleDeleteConfirm(false);
  };

  const checkIngredientInMapping = () => {
    isIngredientInMapping(
      {
        ingredientId: deleteIngredientData?.ingredient_id,
        supplierId: deleteIngredientData?.supplier_id
      },
      {
        onSuccess: (exists: boolean) => {
          if (exists) {
            errorView("Ingredient is used in drawing tool, Please remove it first");
            setVisibleDeleteConfirm(false);
            return;
          }
          confirmDelete();
        }
      }
    );
  };

  const openDetailsModal = (row: any) => {
    setViewIngredientData(row);
    setVisibleDetailsModal(true);
  };

  const openEditModal = (row: any) => {
    setEditIngredientData(row);
    setVisibleEditModal(true);
  };

  const openDeleteConfirm = (row: any) => {
    setDeleteIngredientData(row);
    setVisibleDeleteConfirm(true);
  };

  const navigateDetailsToEdit = () => {
    setVisibleDetailsModal(false);
    openEditModal(viewIngredientData);
  };

  return {
    rows: IngredientRowMapper.toRowViewModels(ingredientList?.result),
    isRowsLoading,
    isMutationInFlight: isMappingCheckLoading || isPutSupplierLoading || isDeleteLoading,
    totalCount: ingredientList?.total || 0,
    totalPages: Math.ceil((ingredientList?.total || 0) / pageLimit) || 1,
    pageNumber,
    pageLimit,
    searchKey,
    isUserSupplier,

    setSearchKey,
    handlePagination: setPageNumber,
    handlePageSize: setPageLimit,
    refetchWithFilters,

    viewIngredientData,
    editIngredientData,
    deleteIngredientData,

    visibleRegisterModal,
    visibleEditModal,
    visibleDetailsModal,
    visibleDeleteConfirm,

    openRegisterModal: () => setVisibleRegisterModal(true),
    closeRegisterModal: () => setVisibleRegisterModal(false),
    openDetailsModal,
    closeDetailsModal: () => setVisibleDetailsModal(false),
    openEditModal,
    closeEditModal: () => setVisibleEditModal(false),
    openDeleteConfirm,
    closeDeleteConfirm: () => {
      setVisibleDeleteConfirm(false);
      setDeleteIngredientData({});
    },

    navigateDetailsToEdit,
    confirmDelete: checkIngredientInMapping,

    ingredientListResult: ingredientList?.result || [],
    refetchIngredients
  };
};
